import { NotAllowedCountry } from 'components/internal/NotAllowedCountry';
import { checkIpInRange } from 'helpers/common';
import type { Country as ECountry } from 'helpers/conf';
import {
  BLACK_LIST_IPS,
  DEFAULT_CURRENCIES,
  HIDDEN_COUNTRIES,
  NOT_ALLOWED_COUNTRIES,
} from 'helpers/conf';
import type { FC } from 'react';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { BeforeInstallPromptEvent } from 'types/common';
import { infoProject as fetchInfoProject } from '../../../../api/common';
import type {
  Country,
  Currency,
  CurrentIp,
  PlayerFields,
} from '../../api/common';
import {
  countries as fetchCountries,
  currentIp as fetchCurrentIp,
  playerFields as fetchPlayerFields,
} from '../../api/common';
import { camelize } from '../../helpers/common';

export interface RegBanner {
  title: string;
  offer1: string;
  offer2?: string;
}

export interface CommonContextData {
  playerFields: PlayerFields | undefined;
  project: { availableReferralSystem: boolean } | null;
  withCaptcha: boolean;
  currencies: Currency[];
  countries: Country[];
  currentIp: CurrentIp | undefined;
  regBanner: RegBanner | null;
}

export interface CommonContextActions {
  fetchProject: () => void;
  setRegBanner: (banner: RegBanner | null) => void;
}

export const CommonContext = createContext<
  CommonContextData & CommonContextActions
>(null as any);

const initialState = {
  playerFields: undefined,
  project: null,
  withCaptcha: false,
  currencies: DEFAULT_CURRENCIES,
  countries: [],
  userGroups: [],
  currentIp: undefined,
  regBanner: null,
};

interface CommonContextProviderProps {
  notAllowedCountries?: { list: string[]; component: JSX.Element };
}

const checkIP = checkIpInRange(BLACK_LIST_IPS);

export const CommonContextProvider: FC<CommonContextProviderProps> = ({
  children,
}) => {
  const [blockService, setBlockService] = useState<boolean>();
  const [currentCode, setCurrentCode] = useState<string>('');

  const [state, setState] = useState<CommonContextData>(initialState);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: currentIp } = await fetchCurrentIp()
          .then(({ data }) => ({ data: JSON.parse(data) }))
          .then(({ data }) => ({ data: camelize(data) as CurrentIp }));

        if (
          currentIp.countryCode in NOT_ALLOWED_COUNTRIES ||
          checkIP(currentIp.ip)
        ) {
          setBlockService(true);
          setCurrentCode(currentIp.countryCode);
          return;
        }

        setBlockService(false);
        setCurrentCode(currentIp.countryCode);

        const [{ data: playerFields }, { data: countries }] = await Promise.all(
          [fetchPlayerFields(), fetchCountries()]
        );

        setState((s) => ({
          ...s,
          playerFields,
          countries: countries.filter(
            (country) => !HIDDEN_COUNTRIES.includes(country.code as ECountry)
          ),
          currentIp,
        }));
      } catch {
        setState(initialState);
      }
    };

    fetchData();
  }, []);

  const ref = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      ref.current = event as BeforeInstallPromptEvent;
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const fetchProject = useCallback(async () => {
    try {
      const { data: project } = await fetchInfoProject();
      setState((prevState: CommonContextData) => ({
        ...prevState,
        project,
      }));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const setRegBanner = useCallback((banner: RegBanner | null) => {
    setState((prev) => ({
      ...prev,
      regBanner: banner,
    }));
  }, []);

  if (blockService == null) return null;
  if (blockService) return <NotAllowedCountry countryCode={currentCode} />;

  return (
    <CommonContext.Provider value={{ ...state, fetchProject, setRegBanner }}>
      <button
        id="installPWAButton"
        onClick={() => {
          if (ref.current) ref.current.prompt();
        }}
        style={{ visibility: 'hidden', position: 'absolute' }}
      />
      {children}
    </CommonContext.Provider>
  );
};
